import React, { useState, useRef, useEffect } from 'react';

const TERMINAL_HISTORY_KEY = 'my-dashboard-terminal-history';

export default function Terminal({ open, onClose, apps }) {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState(['[user@localhost:~]$ Type "help" for commands']);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const inputRef = useRef();

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
    // Load command history from storage
    try {
      const saved = localStorage.getItem(TERMINAL_HISTORY_KEY);
      if (saved) setHistory(JSON.parse(saved));
    } catch (e) { console.warn('Failed to load terminal history'); }
  }, [open]);

  if (!open) return null;

  async function executeCommand(cmd) {
    const parts = cmd.trim().toLowerCase().split(/\s+/);
    const command = parts[0];
    const args = parts.slice(1).join(' ');
    let result = [];
    const prompt = '[user@localhost:~]$ ';

    // Save command to history
    if (cmd.trim()) {
      const newHistory = [cmd, ...history].slice(0, 50); // Keep last 50 commands
      setHistory(newHistory);
      localStorage.setItem(TERMINAL_HISTORY_KEY, JSON.stringify(newHistory));
      setHistoryIndex(-1);
    }

    const api = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/$/, '') : null;
    const token = process.env.REACT_APP_API_TOKEN || null;

    if (command === 'ls' || command === 'list') {
      result.push(prompt + cmd);
      if (api) {
        const url = `${api}/api/sites`;
        result.push(`--> GET ${url}`);
        try {
          const res = await fetch(url, { headers: { ...(token ? { 'x-api-token': token } : {}) } });
          result.push(`<-- ${res.status} ${res.statusText}`);
          if (res.ok) {
            const data = await res.json().catch(()=>({ sites: [] }));
            const list = data.sites || [];
            if (list.length === 0) {
              result.push('Aucun site enregistré');
            } else {
              result = result.concat(list.map((a, i) => `[${i + 1}] ${a.title || a.name} - ${a.url}`));
            }
          } else {
            result.push(`Erreur API: ${res.status}`);
            // fallback to local apps
            if (apps.length === 0) result.push('Aucun site enregistré (fallback)'); else result = result.concat(apps.map((a,i)=>`[${i+1}] ${a.title} - ${a.url}`));
          }
        } catch (err) {
          result.push(`<-- ERROR ${err.message}`);
          if (apps.length === 0) result.push('Aucun site enregistré (offline)'); else result = result.concat(apps.map((a,i)=>`[${i+1}] ${a.title} - ${a.url}`));
        }
      } else {
        if (apps.length === 0) {
          result.push('Aucun site enregistré');
        } else {
          result = result.concat(apps.map((a, i) => `[${i + 1}] ${a.title} - ${a.url}`));
        }
      }
    } else if (command === 'info' || command === 'show') {
      result.push(prompt + cmd);
      const idx = parseInt(args) - 1;
      if (isNaN(idx) || idx < 0) {
        result.push('Usage: info <number>');
      } else {
        if (api) {
          const url = `${api}/api/sites`;
          result.push(`--> GET ${url}`);
          try {
            const res = await fetch(url, { headers: { ...(token ? { 'x-api-token': token } : {}) } });
            result.push(`<-- ${res.status} ${res.statusText}`);
            if (res.ok) {
              const data = await res.json().catch(()=>({ sites: [] }));
              const list = data.sites || [];
              if (idx >= list.length) {
                result.push('Index hors limites');
              } else {
                const a = list[idx];
                result = result.concat([
                  `=== ${a.title || a.name} ===`,
                  `URL: ${a.url}`,
                  `Description: ${a.description || 'N/A'}`,
                  `Status: ${a.status || 'Not checked'}`,
                  `Last check: ${a.lastChecked ? new Date(a.lastChecked).toLocaleString() : 'N/A'}`
                ]);
              }
            } else {
              result.push(`Erreur API: ${res.status}`);
            }
          } catch (err) {
            result.push(`<-- ERROR ${err.message}`);
          }
        } else {
          if (idx >= apps.length) {
            result.push('Usage: info <number>');
          } else {
            const a = apps[idx];
            result = result.concat([
              `=== ${a.title} ===`,
              `URL: ${a.url}`,
              `Description: ${a.description || 'N/A'}`,
              `Status: ${a.status || 'Not checked'}`,
              `Last check: ${a.lastChecked ? new Date(a.lastChecked).toLocaleString() : 'N/A'}`
            ]);
          }
        }
      }
    } else if (command === 'find' || command === 'grep') {
      result.push(prompt + cmd);
      const keyword = args.toLowerCase();
      if (!keyword) {
        result.push('Usage: find <keyword>');
      } else {
        if (api) {
          const url = `${api}/api/sites`;
          result.push(`--> GET ${url}`);
          try {
            const res = await fetch(url, { headers: { ...(token ? { 'x-api-token': token } : {}) } });
            result.push(`<-- ${res.status} ${res.statusText}`);
            if (res.ok) {
              const data = await res.json().catch(()=>({ sites: [] }));
              const found = (data.sites || []).filter(a => (a.title||a.name||'').toLowerCase().includes(keyword) || (a.url||'').toLowerCase().includes(keyword) || ((a.description||'').toLowerCase().includes(keyword)));
              if (found.length === 0) {
                result.push(`Aucun site correspondant à "${keyword}"`);
              } else {
                result = result.concat(found.map((a, i) => `${i + 1}. ${a.title || a.name} (${a.url})`));
              }
            } else {
              result.push(`Erreur API: ${res.status}`);
            }
          } catch (err) {
            result.push(`<-- ERROR ${err.message}`);
          }
        } else {
          const found = apps.filter(a => a.title.toLowerCase().includes(keyword) || a.url.toLowerCase().includes(keyword) || (a.description && a.description.toLowerCase().includes(keyword)));
          if (found.length === 0) {
            result.push(`Aucun site correspondant à "${keyword}"`);
          } else {
            result = result.concat(found.map((a, i) => `${i + 1}. ${a.title} (${a.url})`));
          }
        }
      }
    } else if (command === 'status') {
      result.push(prompt + cmd);
      if (api) {
        const url = `${api}/api/sites`;
        result.push(`--> GET ${url}`);
        try {
          const res = await fetch(url, { headers: { ...(token ? { 'x-api-token': token } : {}) } });
          result.push(`<-- ${res.status} ${res.statusText}`);
          if (res.ok) {
            const data = await res.json().catch(()=>({ sites: [] }));
            result = result.concat((data.sites || []).map(a => `${a.title || a.name}: ${a.status || 'unknown'}`));
          } else {
            result.push(`Erreur API: ${res.status}`);
          }
        } catch (err) {
          result.push(`<-- ERROR ${err.message}`);
        }
      } else {
        result = result.concat(apps.map(a => `${a.title}: ${a.status || 'unknown'}`));
      }
    } else if (command === 'help') {
      result.push(prompt + cmd);
      result = result.concat([
        '=== Commandes disponibles ===',
        'ls, list          - Lister tous les sites',
        'find <mot>        - Chercher un site (titre/URL/description)',
        'info <numero>     - Afficher détails du site (ex: info 1)',
        'status            - Afficher statut de tous les sites',
        'clear             - Effacer l\'affichage',
        'help              - Afficher l\'aide'
      ]);
    } else if (command === 'clear') {
      setOutput([prompt + 'Affichage effacé']);
      setInput('');
      return;
    } else if (command === '') {
      return;
    } else {
      result.push(prompt + cmd);
      result.push(`Commande inconnue: "${command}". Tapez "help" pour l'aide.`);
    }

    setOutput(prev => [...prev, ...result]);
    setInput('');
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      executeCommand(input);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const nextIndex = historyIndex + 1;
      if (nextIndex < history.length) {
        setHistoryIndex(nextIndex);
        setInput(history[nextIndex]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextIndex = historyIndex - 1;
      if (nextIndex >= 0) {
        setHistoryIndex(nextIndex);
        setInput(history[nextIndex]);
      } else if (nextIndex === -1) {
        setHistoryIndex(-1);
        setInput('');
      }
    }
  }

  return (
    <div className="terminal-backdrop" onMouseDown={onClose}>
      <div className="terminal" onMouseDown={e => e.stopPropagation()}>
        <div className="terminal-header">
          <span>Terminal - RedHat Bash</span>
          <button className="icon-btn" onClick={onClose}>✕</button>
        </div>
        <div className="terminal-body">
          {output.map((line, i) => (
            <div key={i} className="terminal-line">{line}</div>
          ))}
        </div>
        <div className="terminal-input">
          <span>[user@localhost:~]$ </span>
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Entrez une commande..."
          />
        </div>
      </div>
    </div>
  );
}
